/**
 * RegulatoryOverviewPage - Jurisdiction compliance dashboard (DRR-207)
 *
 * CONFIGURE section entry point for regulatory configuration.
 * Shows:
 *   - Active jurisdictions with coverage %
 *   - Coverage matrix (sections → rules/FSOs/task groups)
 *   - Recent regulatory changes
 *
 * DRR-207: Data vs Configuration Separation
 * This page is CONFIGURE (behavior), not OPERATE (data).
 * FHIR subgraph shows pure FHIR R4 entities; this page shows overlays.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  Loader,
  Paper,
  Progress,
  RingProgress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronRight,
  IconExclamationMark,
  IconGavel,
  IconScale,
  IconShieldCheck,
} from '@tabler/icons-react';
// TODO: Add useGetRegulatoryCoverageQuery when backend query is implemented
// For now, use mock data to demonstrate the UI

// Jurisdiction metadata (would come from backend in production)
const JURISDICTIONS = [
  {
    id: 'mn-144g',
    name: 'MN 144G',
    fullName: 'Minnesota Assisted Living Licensure',
    level: 'STATE',
    color: 'blue',
  },
  {
    id: 'hipaa',
    name: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    level: 'FEDERAL',
    color: 'indigo',
  },
  {
    id: 'mn-245c',
    name: 'MN 245C',
    fullName: 'Minnesota Background Study Act',
    level: 'STATE',
    color: 'cyan',
  },
];

const STATUS_COLORS: Record<string, string> = {
  FULL: 'green',
  PARTIAL: 'yellow',
  NONE: 'red',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  FULL: <IconCheck size={14} />,
  PARTIAL: <IconExclamationMark size={14} />,
  NONE: <IconAlertTriangle size={14} />,
};

export function RegulatoryOverviewPage() {
  // TODO: Wire to backend query when available
  // const { data: coverageData, loading, error } = useGetRegulatoryCoverageQuery({
  //   variables: { jurisdiction: 'mn-144g' },
  // });
  const loading = false;
  const error = null;

  // Mock coverage data until backend query is wired
  // When backend query is available, replace with: coverageData?.regulatoryCoverage
  const coverage = useMemo(() => ({
    totalSections: 20,
    coveredSections: 18,
    coveragePercent: 90,
    sections: [
      { sectionRef: '144G.41', name: 'Physical Plant', ruleCount: 3, fsoCount: 2, taskGroupCount: 1, status: 'FULL' },
      { sectionRef: '144G.50', name: 'Staffing Requirements', ruleCount: 5, fsoCount: 3, taskGroupCount: 4, status: 'FULL' },
      { sectionRef: '144G.63', name: 'Resident Records', ruleCount: 2, fsoCount: 1, taskGroupCount: 0, status: 'PARTIAL' },
      { sectionRef: '144G.70', name: 'Training Requirements', ruleCount: 4, fsoCount: 2, taskGroupCount: 3, status: 'FULL' },
      { sectionRef: '144G.71', name: 'Delegation', ruleCount: 3, fsoCount: 2, taskGroupCount: 2, status: 'FULL' },
      { sectionRef: '144G.72', name: 'Dementia Care', ruleCount: 2, fsoCount: 1, taskGroupCount: 1, status: 'PARTIAL' },
    ],
  }), []);

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Coverage">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="sm">
            <ThemeIcon size="lg" variant="light" color="indigo">
              <IconGavel size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>Regulatory Configuration</Title>
              <Text size="sm" c="dimmed">
                Jurisdiction-specific compliance configuration (DRR-207)
              </Text>
            </div>
          </Group>
          <Badge variant="light" color="blue" size="lg">
            CONFIGURE
          </Badge>
        </Group>

        {/* Info banner */}
        <Alert icon={<IconScale size={16} />} color="blue" title="Data vs Configuration">
          This section configures HOW the system enforces regulations. FHIR entities (Healthcare section)
          define WHAT data we store. Form Overrides add jurisdiction-specific fields to base FHIR forms.
        </Alert>

        {loading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}

        {/* Active Jurisdictions */}
        <div>
          <Title order={4} mb="md">Active Jurisdictions</Title>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            {JURISDICTIONS.map((jurisdiction) => (
              <Card key={jurisdiction.id} withBorder p="md">
                <Group justify="space-between" mb="md">
                  <Group gap="sm">
                    <ThemeIcon variant="light" color={jurisdiction.color}>
                      <IconShieldCheck size={18} />
                    </ThemeIcon>
                    <div>
                      <Text fw={600}>{jurisdiction.name}</Text>
                      <Text size="xs" c="dimmed">{jurisdiction.level}</Text>
                    </div>
                  </Group>
                  <RingProgress
                    size={50}
                    thickness={5}
                    sections={[{ value: jurisdiction.id === 'mn-144g' ? 90 : 100, color: jurisdiction.color }]}
                    label={
                      <Text size="xs" ta="center" fw={700}>
                        {jurisdiction.id === 'mn-144g' ? '90%' : '100%'}
                      </Text>
                    }
                  />
                </Group>
                <Text size="xs" c="dimmed" mb="sm">{jurisdiction.fullName}</Text>
                <Group gap="xs">
                  <Badge variant="outline" size="xs" color="gray">
                    {jurisdiction.id === 'mn-144g' ? '16 rules' : jurisdiction.id === 'hipaa' ? '12 rules' : '8 rules'}
                  </Badge>
                  <Badge variant="outline" size="xs" color="gray">
                    {jurisdiction.id === 'mn-144g' ? '20 FSOs' : jurisdiction.id === 'hipaa' ? '4 FSOs' : '3 FSOs'}
                  </Badge>
                </Group>
              </Card>
            ))}
          </SimpleGrid>
        </div>

        {/* Coverage Matrix */}
        <Paper withBorder p="md">
          <Group justify="space-between" mb="md">
            <Title order={4}>Coverage Matrix — MN 144G</Title>
            <Anchor component={Link} to="/configure/regulatory/coverage" size="sm">
              View Full Matrix <IconChevronRight size={14} style={{ verticalAlign: 'middle' }} />
            </Anchor>
          </Group>

          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Section</Table.Th>
                <Table.Th>Rules</Table.Th>
                <Table.Th>FSOs</Table.Th>
                <Table.Th>Task Groups</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {coverage.sections?.map((section: { sectionRef: string; name?: string; ruleCount: number; fsoCount: number; taskGroupCount: number; status: string }) => (
                <Table.Tr key={section.sectionRef}>
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm" fw={500}>{section.sectionRef}</Text>
                      {section.name && <Text size="xs" c="dimmed">{section.name}</Text>}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue" size="sm">{section.ruleCount}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="indigo" size="sm">{section.fsoCount}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="cyan" size="sm">{section.taskGroupCount}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={STATUS_COLORS[section.status] ?? 'gray'}
                      size="sm"
                      leftSection={STATUS_ICONS[section.status]}
                    >
                      {section.status}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        {/* Overall Progress */}
        <Paper withBorder p="md">
          <Group justify="space-between" mb="sm">
            <Text fw={600}>Overall Coverage</Text>
            <Text size="sm" c="dimmed">
              {coverage.coveredSections}/{coverage.totalSections} sections
            </Text>
          </Group>
          <Progress
            value={coverage.coveragePercent}
            color={coverage.coveragePercent >= 80 ? 'green' : coverage.coveragePercent >= 50 ? 'yellow' : 'red'}
            size="lg"
          />
          <Text size="xs" c="dimmed" mt="xs">
            {coverage.coveragePercent}% of statutory sections have full implementation (rules + FSOs + task groups)
          </Text>
        </Paper>

        {/* Quick Links */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Card withBorder p="md" component={Link} to="/configure/form-overrides" style={{ textDecoration: 'none' }}>
            <Group gap="sm">
              <ThemeIcon variant="light" color="indigo">
                <IconGavel size={18} />
              </ThemeIcon>
              <div>
                <Text fw={600}>Form Overrides</Text>
                <Text size="xs" c="dimmed">
                  Jurisdiction-specific field additions to base FHIR forms
                </Text>
              </div>
              <IconChevronRight size={18} style={{ marginLeft: 'auto' }} />
            </Group>
          </Card>

          <Card withBorder p="md" component={Link} to="/ferp-core/rulesets" style={{ textDecoration: 'none' }}>
            <Group gap="sm">
              <ThemeIcon variant="light" color="cyan">
                <IconScale size={18} />
              </ThemeIcon>
              <div>
                <Text fw={600}>RuleSets</Text>
                <Text size="xs" c="dimmed">
                  Legislation bundles defining statutory requirements
                </Text>
              </div>
              <IconChevronRight size={18} style={{ marginLeft: 'auto' }} />
            </Group>
          </Card>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}

export default RegulatoryOverviewPage;
